import { useState, useCallback, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';

export interface Campaign {
  id: string;
  partner_id: string;
  facebook_campaign_id: string;
  facebook_ad_id: string | null;
  campaign_name: string;
  ad_name: string | null;
  creative_headline: string | null;
  creative_body: string | null;
  creative_image_url: string | null;
  status: string;
  impressions: number;
  reach: number;
  clicks: number;
  leads_count: number;
  spend_eur: number;
  cpl_eur: number | null;
  ctr: number | null;
  booked_count: number;
  quoted_count: number;
  cost_per_booking_eur: number | null;
  last_synced_at: string | null;
}

export function useCampaigns(partnerId?: string | null) {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastSynced, setLastSynced] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  const loadCampaigns = useCallback(async () => {
    // undefined = no auth context yet, skip; null = operator "fetch all", proceed without filter
    if (partnerId === undefined || !isSupabaseConfigured) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      let query = supabase
        .from('partner_campaigns')
        .select('*')
        .order('spend_eur', { ascending: false });
      if (partnerId !== null) query = query.eq('partner_id', partnerId);
      const { data } = await query;
      const rows = (data ?? []) as Campaign[];
      setCampaigns(rows);
      if (rows[0]?.last_synced_at) setLastSynced(rows[0].last_synced_at);
    } catch {
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, [partnerId]);

  const syncCampaigns = useCallback(async () => {
    if (!partnerId) return;
    setSyncing(true);
    setSyncError(null);
    try {
      const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const SUPABASE_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
      const res = await fetch(`${SUPABASE_URL}/functions/v1/facebook-campaigns-sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_KEY}`,
        },
        body: JSON.stringify({ partner_id: partnerId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok && data?.error) {
        setSyncError(data.error as string);
      }
      await loadCampaigns();
    } catch (err: any) {
      setSyncError(err?.message ?? 'Sync mislukt');
    } finally {
      setSyncing(false);
    }
  }, [partnerId, loadCampaigns]);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  return { campaigns, loading, syncing, lastSynced, syncError, loadCampaigns, syncCampaigns };
}
